import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react';
import { MoreHorizontal, MessageSquareText } from 'lucide-react';
import CommentReactionButtons from '../CommentReactionButtons';
import CommentReactionModal from '../CommentReactionModal';
import LexicalCommentViewer from '../../../../LexicalCommentViewer';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { SharedHistoryContext } from '../../../../context/SharedHistoryContext';
import { CommentEditor } from '../../../../CommentEditor';
import ThreadedReplyItem from '../ThreadedReplyItem';
import TwygsEditorNodes from '../../../../nodes/TwygsEditorNodes';
import getTheme from '../../../../themes/TwygsEditorTheme';
import { convertTimeCreatedAt } from '../../../../../../constants';
import { handleUserClick } from '../../../../../../utils/helper.util';
import VerifiedIcon from '../../../../../../assets/verified-icon.svg';
import FoundingCreatorIcon from '../../../../../../assets/founding_creator_icon.png';
import FlagIcon from '../../../../../../assets/flag.svg';
import EditIcon from '../../../../../../assets/edit-square-black-icon.svg';
import DeleteIcon from '../../../../../../assets/trash-icon.svg';
import ToastBtnMessage from '../../../../../ToastBtnMessage';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import ReportModal from '../ReportModal';
import DeleteModalIcon from '../../../../../../assets/delete-modal-icon.svg';
import UserAvatar from '../../../../../../assets/medium_default_optimized.png';
import ArrowGreenIcon from '../../../../../../assets/arrow-green-icon.svg';
import QuoteBox from './components/QuoteBox';
import CustomModal from '../../../../../Modal/CustomModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { commentApi } from '../../../../../../apis/comment.api';
import RankBadge from '../../../../../../components/RankBadge';
import AwardWriterModal from '../../../../../../components/AwardWriterModal';
import { useMutation } from '@tanstack/react-query';
import { createReputation } from 'src/apis/article.api';
import AwardModalOnSidePanel from '../AwardWriterModalSidebar';
import useRankBadgeTooltip from '../../../../../../hooks/useRankBadgeTooltip';
import { WriterBadgeInfo } from '../../../../../../components/WriterBadgeInfo';
import SensitiveContentCard from '../../../../../../components/SensitiveContentCard';
import ReputationPointComponent from '../../../../../../components/ReputationPointComponent';
import DataLayer from '../../../../../../utils/data-layer';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';
import '../ThreadedReplyItem/styles.css';

// Layout constants
const ROOT_AVATAR_SIZE = 32;
const ROOT_AVATAR_COLUMN_WIDTH = 40;
const _ROOT_AVATAR_CENTER = ROOT_AVATAR_SIZE / 2; // 16px

const CommentItem = ({
  comment,
  currentUser,
  createComment,
  refetchComments,
  updateComment,
  article,
  articleId,
  modalType,
  nodeId,
  handleReaction,
  convertLexicalScope,
  reactToComment,
  deleteComment,
  isReacting
}: any) => {
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [openAwardWriterModal, setOpenAwardWriterModal] = useState(false);
  const [openReplySection, setOpenReplySection] = useState(false);
  const [selectedComment, setSelectedComment] = useState();
  const [listReply, setListReply] = useState([]);
  const [hasShowMore, setHasShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSensitiveContentCard, setShowSensitiveContentCard] = useState(false);
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);
  const [isCreatingComment, setIsCreatingComment] = useState(false);
  const [reactionCounts, setReactionCounts] = useState({});
  const [isReactionModalOpen, setIsReactionModalOpen] = useState(false);
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  const {
    openPopup,
    totalAwardedPoints,
    isTouchDevice,
    handleMouseEnter,
    handleClick,
    handleMouseLeave
  } = useRankBadgeTooltip();

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  });

  const toast = useToast();
  const navigate = useNavigate();

  const editorStateRef = React.useRef(null);
  const { mutateAsync: createPoint, isPending: isCreatingPoint } = useMutation({
    mutationFn: createReputation
  });

  const initialEditorState = comment.content
    ? JSON.stringify(comment?.content?.editorState)
    : undefined;

  const initialConfig = {
    editorState: isReplying ? null : initialEditorState,
    namespace: 'Twygs-comment-editor-reply',
    nodes: [...TwygsEditorNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme: getTheme(true)
  };

  useEffect(() => {
    const loadReactionCounts = async () => {
      try {
        const reactionBreakdown = await commentApi.getCommentReactionBreakdown(comment.id);
        setReactionCounts(reactionBreakdown);
      } catch (error) {
        console.error('Failed to load reaction counts:', error);
      }
    };

    if (comment.id) {
      loadReactionCounts();
    }
  }, [comment.id, comment.reactionNumber, comment.reactedType]);

  const [searchParams] = useSearchParams();

  const handleViewMoreReplies = () => {
    setPagination({
      ...pagination,
      page: pagination.page + 1
    });
  };

  const handleHideAllReplies = () => {
    setOpenReplySection(false);
  };

  const handleEdit = async () => {
    setIsUpdatingComment(true);
    if (editorStateRef.current) {
      const convertedLexicalScope = await convertLexicalScope({
        editorState: editorStateRef.current
      });
      try {
        await updateComment({
          commentId: comment.id,
          content: convertedLexicalScope
        });
        await refetchComments();
        setIsEditing(false);
      } catch (error) {
        console.error('Failed to edit comment:', error);
      } finally {
        setIsUpdatingComment(false);
      }
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleted(true);
      await deleteComment(comment.id);
      setOpenDeleteModal(false);
      toast({
        render: () => (
          <ToastBtnMessage
            title={'Comment successfully deleted!'}
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('Failed to delete comment:', error);
      setIsDeleted(false);
    }
  };

  const handleReply = async (parentId: string) => {
    setIsCreatingComment(true);
    if (editorStateRef.current) {
      const convertedLexicalScope = await convertLexicalScope({
        editorState: editorStateRef.current
      });
      try {
        const result = await createComment({
          articleId,
          nodeId,
          parentCommentId: parentId,
          content: convertedLexicalScope
        });

        if (result) {
          setListReply((prevReplies) => {
            const isDuplicate = prevReplies.some((reply) => reply.id === result.id);
            if (!isDuplicate) {
              return [result, ...prevReplies];
            }
            return prevReplies;
          });

          if (article) {
            const articleType = article.writingType?.name;
            DataLayer.trackArticleComment(articleType, articleId);
          }

          await refetchComments();
          setIsReplying(false);
          editorStateRef.current = null;
        }
      } catch (error) {
        console.error('Failed to post reply:', error);
      } finally {
        setIsCreatingComment(false);
      }
    }
  };

  const handleCreatePoint = async (point: any) => {
    try {
      await createPoint({
        amount: point,
        recipientId: comment.user.id,
        entityId: comment.id,
        entityType: 'comment'
      });

      DataLayer.trackCommentAward(article?.writingType?.name, articleId);

      toast({
        render: () => (
          <ToastBtnMessage
            title="Successfully awarded the comment!"
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
      setOpenAwardWriterModal(false);
      refetchComments();
    } catch (error) {
      toast({
        render: () => (
          <ToastBtnMessage
            title={error?.response?.data?.message}
            isError={true}
            color="jet-black"
            isHiddenButton
          />
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
    }
  };

  const fetchReplies = async () => {
    try {
      setLoading(true);
      const repliedCommentId = searchParams.get('repliedCommentId');
      const result = await commentApi.getReplies(
        comment.id,
        currentUser.id,
        pagination,
        repliedCommentId
      );
      setPagination({
        ...pagination,
        totalPages: result.totalPages,
        totalCount: result.totalCount,
        currentPage: pagination.page
      });

      setListReply((prevReplies) => {
        const combinedReplies =
          pagination.page > 1 ? [...prevReplies, ...result.data] : result.data;
        const uniqueReplies = Array.from(
          new Map(combinedReplies.map((reply) => [reply.id, reply])).values()
        );
        return uniqueReplies;
      });
      setHasShowMore(result.totalPages > pagination.page);
    } catch (error) {
      console.error('Failed to fetch replies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedComment) fetchReplies();
  }, [comment.id, selectedComment, pagination.page]);

  useEffect(() => {
    const element = document.getElementById(searchParams.get('commentId'));
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }, []);

  useEffect(() => {
    if (comment?.moderation?.showWarning !== undefined) {
      setShowSensitiveContentCard(comment.moderation.showWarning);
    }
  }, [comment]);

  useEffect(() => {
    setTimeout(() => {
      const repliedCommentId = searchParams.get('repliedCommentId');
      if (repliedCommentId) {
        const commentId = searchParams.get('commentId');
        document.getElementById(`view-reply-${commentId}`)?.click();
      }
    }, 5000);
  }, [searchParams]);

  const hasVisibleReplies = openReplySection && comment.replies?.length > 0;
  const hasReplies = comment.replies?.length > 0;
  const commentRowRef = React.useRef<HTMLDivElement | null>(null);
  const viewRepliesRowRef = React.useRef<HTMLDivElement | null>(null);
  const [collapsedThreadlineHeight, setCollapsedThreadlineHeight] = useState<number | null>(null);
  const [collapsedTerminalMidY, setCollapsedTerminalMidY] = useState<number | null>(null);

  useEffect(() => {
    if (!hasReplies || openReplySection) {
      setCollapsedThreadlineHeight(null);
      setCollapsedTerminalMidY(null);
      return;
    }

    // Use requestAnimationFrame to ensure DOM is fully painted before measuring
    // This fixes timing issues in production builds with SSR hydration
    const calculatePositions = () => {
      const rowEl = commentRowRef.current;
      const viewEl = viewRepliesRowRef.current;
      if (!rowEl || !viewEl) return;

      const rowRect = rowEl.getBoundingClientRect();
      const viewRect = viewEl.getBoundingClientRect();
      const midY = viewRect.top - rowRect.top + viewRect.height / 2;

      setCollapsedTerminalMidY(midY);
      setCollapsedThreadlineHeight(Math.max(0, midY - ROOT_AVATAR_SIZE));
    };

    // Double RAF ensures we're past the paint cycle
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(calculatePositions);
    });

    return () => cancelAnimationFrame(rafId);
  }, [hasReplies, openReplySection, comment.replies?.length]);

  return (
    <Box
      id={comment.id}
      mb={isEditing ? '0px' : '24px'}
      _last={{ marginBottom: '0px' }}
      className="comment-thread"
    >
      {isEditing ? (
        <Box display="flex">
          <Box
            className="author-thumbnail"
            width={`${ROOT_AVATAR_COLUMN_WIDTH}px`}
            flexShrink={0}
            position="relative"
            display="flex"
            justifyContent="center"
          >
            <Avatar
              w={`${ROOT_AVATAR_SIZE}px`}
              h={`${ROOT_AVATAR_SIZE}px`}
              cursor="pointer"
              src={comment.user.imgUrl || UserAvatar}
              borderRadius="full"
              onClick={(e) => handleUserClick(e, () => navigate(`${comment.user.username}`))}
            />

            {/* YouTube-style segment: absolute threadline + right-half continuation */}
            {hasReplies && (
              <Box
                className="threadline"
                position="absolute"
                bottom="0"
                left="0"
                right="0"
                height={`calc(100% - ${ROOT_AVATAR_SIZE}px)`}
                pointerEvents="none"
              >
                <Box
                  className="continuation"
                  position="absolute"
                  right="0"
                  height="100%"
                  width="50%"
                  boxSizing="border-box"
                  borderLeft="2px solid"
                  borderColor="gray.300"
                />
                {/* Hitbox for hover detection */}
                <Box className="thread-hitbox" />
              </Box>
            )}
          </Box>

          <Box flex={1} minW={0}>
            <LexicalComposer initialConfig={initialConfig as any}>
              <SharedHistoryContext>
                <CommentEditor
                  onPost={handleEdit}
                  currentUser={currentUser}
                  articleId={articleId}
                  editorStateRef={editorStateRef}
                  isHiddenCheckbox
                  isDisabledCancelButton={false}
                  isEditing={isEditing}
                  isLoading={isUpdatingComment}
                  onCancel={() => setIsEditing(false)}
                />
              </SharedHistoryContext>
            </LexicalComposer>
          </Box>
        </Box>
      ) : (
        <Box>
          {/* Comment row (YouTube-style): author-thumbnail + main content */}
          <Box className="comment-row" display="flex" position="relative" ref={commentRowRef}>
            <Box
              className="author-thumbnail"
              width={`${ROOT_AVATAR_COLUMN_WIDTH}px`}
              flexShrink={0}
              position="relative"
              display="flex"
              justifyContent="center"
            >
              <Avatar
                w={`${ROOT_AVATAR_SIZE}px`}
                h={`${ROOT_AVATAR_SIZE}px`}
                cursor="pointer"
                src={comment.user.imgUrl || UserAvatar}
                borderRadius="full"
                onClick={(e) => handleUserClick(e, () => navigate(`${comment.user.username}`))}
              />

              {hasReplies && (
                <Box
                  className="threadline"
                  position="absolute"
                  left="0"
                  right="0"
                  top={`${ROOT_AVATAR_SIZE}px`}
                  height={
                    openReplySection || collapsedThreadlineHeight === null
                      ? `calc(100% - ${ROOT_AVATAR_SIZE}px)`
                      : `${collapsedThreadlineHeight - 15}px`
                  }
                  pointerEvents="none"
                >
                  <Box
                    className="continuation"
                    position="absolute"
                    right="0"
                    height="100%"
                    width="50%"
                    boxSizing="border-box"
                    borderLeft="2px solid"
                    borderColor="gray.300"
                  />
                  {/* Hitbox for hover detection */}
                  <Box className="thread-hitbox" />
                </Box>
              )}
            </Box>

            {/* Collapsed-state terminal elbow (gutter-only, so it can't cut into text) */}
            {hasReplies && !openReplySection && collapsedTerminalMidY !== null && (
              <Box
                className="collapsed-elbow"
                position="absolute"
                left={`${ROOT_AVATAR_COLUMN_WIDTH / 2}px`}
                top={`${collapsedTerminalMidY - 20}px`}
                width={`${ROOT_AVATAR_COLUMN_WIDTH / 2}px`}
                height="20px"
                borderLeft="2px solid"
                borderBottom="2px solid"
                borderColor="gray.300"
                borderBottomLeftRadius="20px"
                boxSizing="border-box"
                pointerEvents="none"
              >
                {/* Hitbox for hover detection */}
                <Box className="thread-hitbox" />
              </Box>
            )}

            <Box flex={1} minW={0}>
              {/* Comment body */}
              <Box className="comment-body">
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Box
                      fontWeight="medium"
                      display="flex"
                      flexWrap="wrap"
                      alignItems="center"
                      cursor="pointer"
                      gap={1}
                      fontSize="sm"
                      onClick={(e) =>
                        handleUserClick(e, () => navigate(`/${comment.user.username}`))
                      }
                    >
                      <span className="hover:text-growth-green-500 transition duration-200">
                        {comment.user.firstName}
                      </span>
                      {comment.user.isVerifiedAuthor && <Image ml={1} src={VerifiedIcon} alt="" />}
                      {comment.user.isFoundingCreator && (
                        <Image
                          ml={1}
                          src={FoundingCreatorIcon}
                          alt="Founding Creator"
                          title="Founding Creator"
                          w={4}
                          h={4}
                        />
                      )}
                      {comment.user.username && (
                        <span className="font-[600] text-xs text-02-dim-gray">
                          @{comment.user.username}
                        </span>
                      )}
                      {comment.userId === article?.creator?.id ? (
                        <Box
                          fontSize="sm"
                          height="24px"
                          lineHeight="16px"
                          bg="growth-green"
                          color="#EDF7EE"
                          px={2}
                          py={1}
                          borderRadius="full"
                        >
                          Author
                        </Box>
                      ) : (
                        currentUser?.id === comment.userId && (
                          <div className="px-2 py-1 rounded-[80px] bg-growth-green-50 text-xs text-growth-green-900 font-[600]">
                            You
                          </div>
                        )
                      )}
                      <div
                        className="relative"
                        onMouseEnter={() => !isTouchDevice && handleMouseEnter(comment.user.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(comment.user.id);
                        }}
                        onMouseLeave={() => !isTouchDevice && handleMouseLeave()}
                      >
                        <RankBadge rank={comment?.user?.currentRank} hiddenRankText />
                        {openPopup && (
                          <div className="absolute top-[34px] md:top-[-20px] left-[-50%] md:left-[50px] z-10">
                            <WriterBadgeInfo
                              rank={comment?.user?.currentRank}
                              totalReceivedPoint={totalAwardedPoints.totalReceivedPoint}
                              totalReceivedUsers={totalAwardedPoints.totalReceivedUsers}
                            />
                          </div>
                        )}
                      </div>
                    </Box>

                    <Box fontSize="xs" color="dim-gray">
                      {convertTimeCreatedAt(comment.createdAt)} {comment.isEdited && '(edited)'}
                    </Box>
                  </VStack>
                  {!isDeleted && (
                    <Menu>
                      <MenuButton
                        as={Button}
                        variant="unstyled"
                        border="none"
                        boxShadow="none"
                        h="32px"
                        size="sm"
                        rounded="full"
                        _hover={{ bg: '#F0F0F0' }}
                      >
                        <MoreHorizontal className="ml-[6px]" size={20} />
                      </MenuButton>
                      {comment.userId === currentUser.id ? (
                        <MenuList minW="unset" p="0.625rem" shadow="md" rounded="12px">
                          <MenuItem
                            _hover={{ bgColor: 'anti-flash-white' }}
                            rounded="0.5rem"
                            py="8px"
                            px="10px"
                            fontSize="sm"
                            fontWeight={900}
                            _focus={{ bgColor: 'anti-flash-white' }}
                            onClick={() => setIsEditing(true)}
                            icon={<img src={EditIcon} alt="" className="w-5 h-5" />}
                          >
                            Edit comment
                          </MenuItem>
                          <MenuItem
                            _hover={{ bgColor: 'anti-flash-white' }}
                            rounded="0.5rem"
                            py="8px"
                            px="10px"
                            fontSize="sm"
                            fontWeight={900}
                            _focus={{ bgColor: 'anti-flash-white' }}
                            icon={<img src={DeleteIcon} alt="" className="w-5 h-5" />}
                            onClick={() => setOpenDeleteModal(true)}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      ) : (
                        <MenuList minW="unset" p="0.625rem" shadow="md" rounded="12px">
                          <MenuItem
                            _hover={{ bgColor: 'anti-flash-white' }}
                            rounded="0.5rem"
                            py="8px"
                            px="10px"
                            fontSize="sm"
                            fontWeight={900}
                            _focus={{ bgColor: 'anti-flash-white' }}
                            icon={<img src={FlagIcon} alt="" className="w-5 h-5" />}
                            onClick={() => setOpenReportModal(true)}
                          >
                            Report comment
                          </MenuItem>
                        </MenuList>
                      )}
                    </Menu>
                  )}
                </HStack>

                {comment.quote && <QuoteBox quote={comment.quote} />}

                <Box>
                  {!isEditing && (
                    <Box
                      color="jet-black"
                      mt={2}
                      position="relative"
                      display="flex"
                      justifyContent="left"
                    >
                      {!isDeleted ? (
                        <LexicalCommentViewer
                          content={comment.content}
                          className={`min-h-0 ${showSensitiveContentCard && !comment?.isShowSensitive && 'blur-lg'}`}
                        />
                      ) : (
                        <Text fontStyle="italic" color="dim-gray" fontWeight={400}>
                          This comment has been deleted
                        </Text>
                      )}

                      <div
                        className={`${showSensitiveContentCard && !comment?.isShowSensitive ? 'block' : 'hidden'} absolute top-[20%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-10`}
                      >
                        <SensitiveContentCard
                          isComment
                          actionButton={comment?.moderation?.actionButton}
                          primarySource={comment?.moderation?.primarySource}
                          primaryViolation={comment?.moderation?.primaryViolation}
                          setShowSensitiveContentCard={setShowSensitiveContentCard}
                          commentId={comment?.id}
                          articleId={articleId}
                          isShowSensitive={comment?.isShowSensitive}
                        />
                      </div>
                    </Box>
                  )}
                  {!isEditing && !isDeleted && (
                    <HStack spacing={4} mt={2} flexWrap="wrap">
                      <CommentReactionButtons
                        comment={comment}
                        isReacting={isReacting}
                        handleReaction={handleReaction}
                        reactionCounts={reactionCounts}
                        onOpenReactionModal={() => setIsReactionModalOpen(true)}
                      />

                      <Button
                        display="flex"
                        alignItems="center"
                        variant="unset"
                        border="none"
                        boxShadow="none"
                        height="auto"
                        fontSize="xs"
                        _hover={{ opacity: 0.7 }}
                        onClick={() => setIsReplying(!isReplying)}
                      >
                        <MessageSquareText size={16} />
                        <Text ml={1}>Reply</Text>
                      </Button>
                      {comment.userId !== currentUser.id && (
                        <ReputationPointComponent
                          reputationPoints={comment.reputationPoint}
                          setOpenAwardWriterModal={setOpenAwardWriterModal}
                        />
                      )}
                    </HStack>
                  )}

                  {comment.replies.length > 0 && !openReplySection && (
                    <Box
                      onClick={() => {
                        setSelectedComment(comment.id);
                        setOpenReplySection(!openReplySection);
                      }}
                      ref={viewRepliesRowRef}
                      cursor="pointer"
                      _hover={{ opacity: 0.8 }}
                      display="flex"
                      alignItems="center"
                      mt={2}
                      gap={2}
                    >
                      {comment.isAuthorCommented && (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            w="24px"
                            h="24px"
                            cursor="pointer"
                            src={article?.creator?.imgUrl || UserAvatar}
                            borderRadius="full"
                          />
                          {article?.creator?.isVerifiedAuthor && (
                            <Image ml={1} src={VerifiedIcon} alt="" />
                          )}
                          <Box
                            fontSize="sm"
                            height="24px"
                            lineHeight="16px"
                            bg="growth-green"
                            color="#EDF7EE"
                            px={2}
                            py={1}
                            borderRadius="full"
                          >
                            Author
                          </Box>
                        </Box>
                      )}
                      <Text
                        id={`view-reply-${comment.id}`}
                        fontSize="sm"
                        color="growth-green"
                        fontWeight={900}
                      >
                        View {comment.thread.replyCount}{' '}
                        {comment.thread.replyCount === 1 ? 'reply' : 'replies'}
                      </Text>
                      <Image src={ArrowGreenIcon} alt="" className="w-5 h-5 rotate-90" />
                    </Box>
                  )}

                  {loading && <Spinner size="sm" color="growth-green" />}

                  {isReplying && (
                    <Box mt={4}>
                      <LexicalComposer initialConfig={initialConfig as any}>
                        <SharedHistoryContext>
                          <CommentEditor
                            onPost={() => handleReply(comment.id)}
                            currentUser={currentUser}
                            editorStateRef={editorStateRef}
                            isHiddenCheckbox
                            replyTo={comment.user.firstName}
                            isDisabledCancelButton={false}
                            isLoading={isCreatingComment}
                            onCancel={() => setIsReplying(false)}
                          />
                        </SharedHistoryContext>
                      </LexicalComposer>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Replies container (curve lives here and connects back to the root segment) */}
          {hasVisibleReplies && (
            <Box className="replies-container" mt={0} ml={`${ROOT_AVATAR_COLUMN_WIDTH}px`}>
              {listReply?.map((reply, index) => (
                <ThreadedReplyItem
                  key={reply.id}
                  comment={reply}
                  currentUser={currentUser}
                  createComment={createComment}
                  reactToComment={(data) => {
                    if (!userId) return UnauthorizedDispatcher();
                    return reactToComment?.(data);
                  }}
                  updateComment={updateComment}
                  deleteComment={deleteComment}
                  fetchReplies={fetchReplies}
                  refetchComments={refetchComments}
                  article={article}
                  articleId={articleId}
                  nodeId={nodeId}
                  convertLexicalScope={convertLexicalScope}
                  modalType={modalType}
                  depth={reply.depth || 1}
                  maxDepth={3}
                  isLastChild={index === listReply.length - 1}
                  parentAvatarCenter={ROOT_AVATAR_COLUMN_WIDTH / 2}
                  parentColumnWidth={ROOT_AVATAR_COLUMN_WIDTH}
                  onReplyAdded={(newReply) => {
                    setListReply((prev) => {
                      const updateChildren = (replies) => {
                        return replies.map((r) => {
                          if (r.id === newReply.parentCommentId) {
                            return {
                              ...r,
                              children: [...(r.children || []), newReply]
                            };
                          }
                          if (r.children?.length > 0) {
                            return {
                              ...r,
                              children: updateChildren(r.children)
                            };
                          }
                          return r;
                        });
                      };
                      return updateChildren(prev);
                    });
                  }}
                />
              ))}

              {hasShowMore && (
                <Text
                  onClick={handleViewMoreReplies}
                  fontSize="sm"
                  color="growth-green"
                  mt={3}
                  ml="24px"
                  fontWeight={900}
                  cursor="pointer"
                  _hover={{ opacity: 0.8 }}
                >
                  View {pagination.totalCount - pagination.currentPage * pagination.limit} more{' '}
                  {pagination.totalCount === 1 ? 'reply' : 'replies'}
                </Text>
              )}

              {!hasShowMore && !loading && (
                <Text
                  onClick={handleHideAllReplies}
                  fontSize="sm"
                  color="growth-green"
                  mt={3}
                  ml="24px"
                  fontWeight={900}
                  cursor="pointer"
                  _hover={{ opacity: 0.8 }}
                >
                  Hide all replies
                </Text>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Modals */}
      {openDeleteModal ? (
        modalType === 'general' ? (
          <CustomModal
            isOpen
            title="Delete Comment?"
            onClick={() => setOpenDeleteModal(false)}
            hideFooter
            maxW="407px"
          >
            <div className="w-full flex flex-col items-center">
              <img src={DeleteModalIcon} alt="" />
              <p className="text-lg font-[600] mt-4 text-center px-6">
                This action will permanently delete your comment
              </p>
              <div className="flex gap-[10px] mt-6 w-full">
                <Button
                  bg="white"
                  border="2px solid"
                  borderColor="growth-green"
                  rounded="8px"
                  w="100%"
                  onClick={() => setOpenDeleteModal(false)}
                  h="42px"
                  color="growth-green"
                  fontWeight={900}
                  fontSize="md"
                  _hover={{ opacity: '0.9' }}
                >
                  Cancel
                </Button>
                <Button
                  bg="error-red"
                  color="white"
                  border="1px solid"
                  borderColor="error-red"
                  rounded="8px"
                  w="100%"
                  h="42px"
                  onClick={handleDelete}
                  fontWeight={900}
                  fontSize="md"
                  _hover={{ opacity: '0.7' }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </CustomModal>
        ) : (
          <ConfirmDeleteModal setOpen={setOpenDeleteModal} handleDelete={handleDelete} />
        )
      ) : null}

      {openReportModal ? (
        modalType === 'general' ? (
          <Modal isOpen={openReportModal} isCentered onClose={() => setOpenReportModal(false)}>
            <ModalOverlay />
            <ModalContent h="80vh" maxW="800px">
              <ModalBody p={0} overflow="auto" rounded="12px">
                <ReportModal
                  commentId={comment.id}
                  targetUserId={comment.userId}
                  username={comment.user.username}
                  setOpen={setOpenReportModal}
                />
              </ModalBody>
            </ModalContent>
          </Modal>
        ) : (
          <Box
            bg="white"
            position="absolute"
            top="0"
            right="0"
            bottom="0"
            zIndex="1000"
            w="100%"
            h="100%"
          >
            <ReportModal
              commentId={comment.id}
              targetUserId={comment.userId}
              username={comment.user.username}
              setOpen={setOpenReportModal}
            />
          </Box>
        )
      ) : null}

      {openAwardWriterModal ? (
        modalType === 'general' ? (
          <AwardWriterModal
            isOpen
            handleCreatePoint={handleCreatePoint}
            title={`Recognize ${comment.user.username || comment.user.firstName}'s comment`}
            onClose={() => setOpenAwardWriterModal(false)}
            isLoading={isCreatingPoint}
            previousPoint={comment?.reputationPoint?.given}
            alreadyGivePoint={comment?.reputationPoint?.given !== 0}
            dailyLimits={comment?.reputationPoint?.dailyLimits}
            contentType="comment"
          />
        ) : (
          <AwardModalOnSidePanel
            previousPoint={comment?.reputationPoint?.given}
            setOpen={setOpenAwardWriterModal}
            handleCreatePoint={handleCreatePoint}
            onClose={() => setOpenAwardWriterModal(false)}
            isLoading={isCreatingPoint}
            alreadyGivePoint={comment?.reputationPoint?.given !== 0}
            title={`Recognize ${comment.user.username || comment.user.firstName}'s comment:`}
            dailyLimits={comment?.reputationPoint?.dailyLimits}
          />
        )
      ) : null}

      <CommentReactionModal
        isOpen={isReactionModalOpen}
        onClose={() => setIsReactionModalOpen(false)}
        commentId={comment.id}
        totalReactions={comment.reactionNumber || 0}
      />
    </Box>
  );
};

export default CommentItem;
